import React, { useState, useEffect, useRef } from 'react';
import {
  Globe,
  Layers,
  CreditCard,
  ExternalLink,
  ChevronDown,
  Plus,
  CheckCircle2,
  AlertTriangle,
  Building2,
  Ship,
  Sparkles,
  LogOut,
  ArrowLeft,
  MailCheck,
  Menu,
  X,
  Bell,
  ShieldCheck,
  MessageSquare,
  Settings,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';
import { getSiteDisplayAddress } from '../../utils/security';

interface NavbarProps {
  currentTab?: string;
  onSelectTab?: (tab: string) => void;
  onOpenLiveSite?: () => void;
  onOpenNewSiteWizard?: () => void;
  onOpenWizard?: () => void;
  onOpenAiWizard?: () => void;
  onOpenLivePreview?: () => void;
  onOpenBilling?: () => void;
  onReturnToMarketing?: () => void;
  onToggleMobileMenu?: () => void;
  isMobileMenuOpen?: boolean;
}

const PAGE_TITLES: Record<string, { title: string; category?: string }> = {
  dashboard: { title: 'Dashboard', category: 'Genel Bakış' },
  'business-profile': { title: 'İşletme Profili', category: 'Firma Bilgileri' },
  services: { title: 'Hizmet Yönetimi', category: 'Katalog' },
  'design-system': { title: 'Tasarım Sistemi', category: 'Görsel Kimlik' },
  design: { title: 'Tasarım Sistemi', category: 'Görsel Kimlik' },
  testimonials: { title: 'Müşteri Yorumları', category: 'Referanslar' },
  'local-seo': { title: 'Yerel SEO & Şema', category: 'Arama Motoru' },
  yachts: { title: 'Yat Filosu', category: 'Filo Yönetimi' },
  reservations: { title: 'Rezervasyon Talepleri', category: 'İşlemler' },
  calendar: { title: 'Rezervasyon Takvimi', category: 'Müsaitlik' },
  communications: { title: 'Gelen Mesajlar', category: 'İletişim & Formlar' },
  blog: { title: 'Blog & Makaleler', category: 'İçerik Pazarlaması' },
  domains: { title: 'Domain & SSL', category: 'Alan Adı Güvenliği' },
  media: { title: 'Medya Kütüphanesi', category: 'Görseller & Dosyalar' },
  seo: { title: 'SEO & Sıralama', category: 'Arama Optimizasyonu' },
  billing: { title: 'Abonelik & Ödemeler', category: 'Faturalandırma' },
  settings: { title: 'Hesap & Ayarlar', category: 'Profil Ayarları' },
  editor: { title: 'Görsel Editör', category: 'Tasarım Alanı' },
};

export const Navbar: React.FC<NavbarProps> = ({
  currentTab = 'dashboard',
  onSelectTab,
  onOpenLiveSite,
  onOpenNewSiteWizard,
  onOpenWizard,
  onOpenAiWizard,
  onOpenLivePreview,
  onOpenBilling,
  onReturnToMarketing,
  onToggleMobileMenu,
  isMobileMenuOpen = false,
}) => {
  const {
    currentUser,
    isEmailVerified,
    sendVerificationEmail,
    logout,
  } = useAuth();
  const { activeSite, sites, setActiveSiteById, unreadCommunicationsCount = 0 } = useSite();
  const [activeDropdown, setActiveDropdown] = useState<'site' | 'notifications' | 'user' | null>(null);
  const [verifyingNotice, setVerifyingNotice] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = (name: 'site' | 'notifications' | 'user') => {
    setActiveDropdown((prev) => (prev === name ? null : name));
  };

  const closeDropdowns = () => {
    setActiveDropdown(null);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        closeDropdowns();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDropdowns();
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleSelectTab = (tab: string) => {
    closeDropdowns();
    if (onSelectTab) {
      onSelectTab(tab);
    }
  };

  const handleOpenLive = () => {
    closeDropdowns();
    if (onOpenLivePreview) onOpenLivePreview();
    else if (onOpenLiveSite) onOpenLiveSite();
  };

  const handleOpenWizard = () => {
    closeDropdowns();
    if (onOpenWizard) onOpenWizard();
    else if (onOpenNewSiteWizard) onOpenNewSiteWizard();
  };

  const handleSendVerification = async () => {
    try {
      await sendVerificationEmail();
      setVerifyingNotice('Doğrulama bağlantısı e-postanıza gönderildi!');
      setTimeout(() => setVerifyingNotice(null), 4000);
    } catch (e: any) {
      setVerifyingNotice('Bağlantı gönderilemedi: ' + (e.message || 'Hata'));
      setTimeout(() => setVerifyingNotice(null), 4000);
    }
  };

  const handleLogout = async () => {
    closeDropdowns();
    await logout();
    if (onReturnToMarketing) {
      onReturnToMarketing();
    } else {
      window.location.pathname = '/giris';
    }
  };

  const pageInfo = PAGE_TITLES[currentTab] || { title: 'Dashboard', category: 'Panel' };

  return (
    <header
      ref={navRef}
      className="sticky top-0 z-40 w-full bg-slate-900 border-b border-slate-800 text-white select-none shrink-0 shadow-sm"
    >
      {/* Unverified Email Alert Banner if needed */}
      {!isEmailVerified && currentUser && (
        <div className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-1 text-xs text-amber-300 flex items-center justify-between">
          <div className="flex items-center gap-2 truncate">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
            <span className="truncate">
              E-posta adresiniz ({currentUser.email}) henüz doğrulanmadı.
            </span>
          </div>
          <div className="flex items-center gap-3 shrink-0 ml-2">
            {verifyingNotice ? (
              <span className="text-emerald-400 font-semibold text-[11px]">{verifyingNotice}</span>
            ) : (
              <button
                onClick={handleSendVerification}
                className="underline hover:text-white font-medium flex items-center gap-1 text-[11px]"
              >
                <MailCheck className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Doğrulama E-postası Gönder</span>
                <span className="sm:hidden">Doğrula</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Header Container: 3 Zones (LEFT, CENTER, RIGHT) with full width and consistent padding */}
      <div className="w-full h-16 px-3 sm:px-5 lg:px-6 flex items-center justify-between gap-2 sm:gap-4 min-w-0">
        
        {/* ========================================================================= */}
        {/* 1. LEFT ZONE: Adiosoft Logo & Mobile Drawer Toggle                        */}
        {/* ========================================================================= */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Hamburger Menu Toggle (Mobile & Tablet: < lg) */}
          {onToggleMobileMenu && (
            <button
              id="mobile-sidebar-toggle-btn"
              onClick={onToggleMobileMenu}
              className="lg:hidden p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition shrink-0 focus:outline-hidden"
              aria-label={isMobileMenuOpen ? 'Menüyü Kapat' : 'Menüyü Aç'}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}

          {/* Adiosoft Brand Logo: Aligned to the far left with consistent padding */}
          <div
            id="adiosoft-header-brand"
            onClick={() => handleSelectTab('dashboard')}
            className="flex items-center gap-2 sm:gap-2.5 cursor-pointer shrink-0 select-none group"
            title="Adiosoft Dashboard"
          >
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-gradient-to-tr from-blue-600 via-indigo-600 to-sky-500 flex items-center justify-center shadow-md shadow-blue-500/20 group-hover:scale-105 transition-transform duration-200">
              <Sparkles className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-white" />
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <span className="font-extrabold text-base sm:text-lg tracking-tight text-white group-hover:text-blue-200 transition-colors">
                Adiosoft
              </span>
              <span className="hidden xs:inline-flex text-[9px] sm:text-[10px] uppercase tracking-wider font-bold px-1.5 sm:px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-400/30">
                Panel
              </span>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* 2. CENTER ZONE: Dashboard / Current Page Title                            */}
        {/* ========================================================================= */}
        <div className="flex-1 min-w-0 flex items-center justify-center sm:justify-start lg:justify-center px-1 sm:px-2">
          <div className="flex items-center gap-2 min-w-0 truncate">
            <h1 className="text-xs sm:text-sm md:text-base font-bold text-white tracking-tight truncate">
              {pageInfo.title}
            </h1>
            {activeSite && (
              <span className="hidden xl:inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-slate-800 text-slate-300 border border-slate-700/80 truncate max-w-[200px]">
                {activeSite.industry === 'YACHT_RENTAL' ? (
                  <Ship className="w-3 h-3 text-cyan-400 shrink-0" />
                ) : (
                  <Building2 className="w-3 h-3 text-emerald-400 shrink-0" />
                )}
                <span className="truncate">{activeSite.name}</span>
              </span>
            )}
          </div>
        </div>

        {/* ========================================================================= */}
        {/* 3. RIGHT ZONE: Site Selector, Live View, Notifications, User Profile      */}
        {/* ========================================================================= */}
        <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
          
          {/* Site Selector Dropdown (Desktop / Tablet) */}
          {activeSite && (
            <div className="relative hidden md:block">
              <button
                id="site-selector-btn"
                onClick={() => toggleDropdown('site')}
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700/90 border border-slate-700 text-xs font-medium transition max-w-[130px] sm:max-w-[170px]"
                title="Aktif Siteyi Değiştir"
              >
                {activeSite.industry === 'CORPORATE' ? (
                  <Building2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                ) : (
                  <Ship className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                )}
                <span className="font-semibold text-slate-100 truncate block text-[11px] sm:text-xs">
                  {activeSite.name}
                </span>
                <ChevronDown className="w-3 h-3 text-slate-400 shrink-0 ml-auto" />
              </button>

              {activeDropdown === 'site' && (
                <div className="absolute right-0 top-full mt-2 w-72 sm:w-80 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl p-2.5 z-50 animate-in fade-in zoom-in-95 duration-100">
                  <div className="text-[11px] font-semibold text-slate-400 px-3 py-1.5 uppercase tracking-wider">
                    Mevcut Siteleriniz ({sites.length})
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-1 my-1 custom-scrollbar">
                    {sites.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => {
                          setActiveSiteById(s.id);
                          closeDropdowns();
                        }}
                        className={`w-full text-left px-3 py-2 rounded-xl flex items-center justify-between text-xs transition ${
                          activeSite.id === s.id
                            ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30 font-semibold'
                            : 'hover:bg-slate-800 text-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 truncate min-w-0 pr-2">
                          {s.industry === 'CORPORATE' ? (
                            <Building2 className="w-4 h-4 text-emerald-400 shrink-0" />
                          ) : (
                            <Ship className="w-4 h-4 text-cyan-400 shrink-0" />
                          )}
                          <div className="truncate">
                            <div className="font-medium text-slate-100 truncate">{s.name}</div>
                            <div className="text-[10px] text-slate-400 truncate">{getSiteDisplayAddress(s)}</div>
                          </div>
                        </div>
                        {activeSite.id === s.id && <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0" />}
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-slate-800 pt-2 mt-1 space-y-1.5">
                    <button
                      onClick={() => {
                        handleSelectTab('sites');
                      }}
                      className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition"
                    >
                      <Layers className="w-3.5 h-3.5 text-sky-400" />
                      Tüm Sitelerimi Yönet ({sites.length})
                    </button>
                    <button
                      onClick={() => {
                        closeDropdowns();
                        if (onOpenAiWizard) onOpenAiWizard();
                        else handleOpenWizard();
                      }}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-500 hover:to-sky-500 text-white text-xs font-bold shadow-md shadow-indigo-500/20 transition"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      AI ile Site Oluştur
                    </button>
                    <button
                      onClick={() => {
                        closeDropdowns();
                        handleOpenWizard();
                      }}
                      className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-medium transition"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Manuel Şablon Seç
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Canlı Site (Live View) Button */}
          <button
            id="view-live-site-top-btn"
            onClick={handleOpenLive}
            title="Canlı Web Sitesini Aç"
            className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg bg-emerald-600/15 hover:bg-emerald-600/25 text-emerald-300 border border-emerald-500/30 text-xs font-semibold transition shadow-xs shrink-0"
          >
            <ExternalLink className="w-3.5 h-3.5 shrink-0" />
            <span className="hidden sm:inline whitespace-nowrap">Canlı Site</span>
          </button>

          {/* Bildirimler (Notifications) Bell Button & Dropdown */}
          <div className="relative">
            <button
              id="notifications-menu-btn"
              onClick={() => toggleDropdown('notifications')}
              title="Bildirimler ve Sistem Durumu"
              className="relative p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 transition shrink-0"
              aria-label="Bildirimler"
            >
              <Bell className="w-4 h-4 shrink-0" />
              {unreadCommunicationsCount > 0 ? (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white font-black text-[9px] flex items-center justify-center animate-pulse shadow-sm">
                  {unreadCommunicationsCount}
                </span>
              ) : (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-emerald-500" />
              )}
            </button>

            {activeDropdown === 'notifications' && (
              <div className="absolute right-0 top-full mt-2 w-72 sm:w-80 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl p-3 z-50 animate-in fade-in zoom-in-95 duration-100">
                <div className="flex items-center justify-between pb-2.5 border-b border-slate-800">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-blue-400" />
                    <span className="text-xs font-bold text-white">Bildirimler</span>
                  </div>
                  {unreadCommunicationsCount > 0 && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/30">
                      {unreadCommunicationsCount} Yeni
                    </span>
                  )}
                </div>

                <div className="py-2 space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
                  {unreadCommunicationsCount > 0 ? (
                    <div
                      onClick={() => handleSelectTab('communications')}
                      className="p-2.5 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/25 cursor-pointer transition"
                    >
                      <div className="flex items-start gap-2.5">
                        <MessageSquare className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                        <div>
                          <div className="text-xs font-semibold text-white">Yeni İletişim Mesajı</div>
                          <div className="text-[11px] text-slate-300 mt-0.5">
                            {unreadCommunicationsCount} adet yanıt bekleyen müşteri mesajınız var.
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Operational Health item */}
                  <div className="p-2.5 rounded-xl bg-slate-800/60 border border-slate-700/60">
                    <div className="flex items-start gap-2.5">
                      <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                      <div>
                        <div className="text-xs font-semibold text-emerald-300">Sistem & Altyapı Aktif</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          Web siteniz, SSL sertifikası ve iletişim sunucuları sorunsuz çalışıyor.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Domain summary */}
                  {activeSite && (
                    <div
                      onClick={() => handleSelectTab('domains')}
                      className="p-2.5 rounded-xl bg-slate-800/40 hover:bg-slate-800 border border-slate-700/50 cursor-pointer transition"
                    >
                      <div className="flex items-start gap-2.5">
                        <Globe className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-slate-200">Alan Adı Durumu</div>
                          <div className="text-[11px] text-slate-400 truncate mt-0.5">
                            {getSiteDisplayAddress(activeSite)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-2 border-t border-slate-800">
                  <button
                    onClick={() => handleSelectTab('site-status')}
                    className="w-full py-1.5 text-center text-xs font-medium text-blue-400 hover:text-blue-300 transition"
                  >
                    Tüm Sistem Durumunu İncele →
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Kullanıcı / Profil Menüsü */}
          <div className="relative">
            <button
              id="user-profile-menu-btn"
              onClick={() => toggleDropdown('user')}
              className="flex items-center gap-2 px-1.5 sm:px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-xs transition shrink-0"
              title="Kullanıcı Menüsü"
            >
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 border border-slate-600 flex items-center justify-center font-bold text-white text-xs shrink-0">
                {currentUser?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
              </div>
              <div className="text-left hidden md:block max-w-[120px] truncate">
                <div className="font-semibold text-slate-100 leading-tight truncate">
                  {currentUser?.displayName || 'Kullanıcı'}
                </div>
                <div className="text-[10px] text-slate-400">
                  Müşteri Hesabı
                </div>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            </button>

            {activeDropdown === 'user' && (
              <div className="absolute right-0 top-full mt-2 w-72 sm:w-80 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl p-3 z-50 animate-in fade-in zoom-in-95 duration-100">
                <div className="pb-3 border-b border-slate-800 mb-2">
                  <div className="text-xs font-bold text-white truncate">
                    {currentUser?.displayName || 'Kullanıcı'}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">{currentUser?.email}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">
                      Müşteri Hesabı
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                        isEmailVerified
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                      }`}
                    >
                      {isEmailVerified ? '✓ Doğrulandı' : '⚠ Doğrulanmadı'}
                    </span>
                  </div>
                </div>

                <div className="space-y-1">
                  <button
                    onClick={() => handleSelectTab('settings')}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-slate-300 hover:bg-slate-800 transition"
                  >
                    <Settings className="w-4 h-4 text-slate-400" />
                    <span>Hesap & Güvenlik Ayarları</span>
                  </button>

                  <button
                    onClick={() => {
                      if (onOpenBilling) {
                        closeDropdowns();
                        onOpenBilling();
                      } else {
                        handleSelectTab('billing');
                      }
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-slate-300 hover:bg-slate-800 transition"
                  >
                    <CreditCard className="w-4 h-4 text-slate-400" />
                    <span>Abonelik & Faturalandırma</span>
                  </button>

                  {onReturnToMarketing && (
                    <button
                      onClick={() => {
                        closeDropdowns();
                        onReturnToMarketing();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-slate-300 hover:bg-slate-800 transition"
                    >
                      <ArrowLeft className="w-4 h-4 text-slate-400" />
                      <span>Tanıtım Sitesine Dön</span>
                    </button>
                  )}
                </div>

                <div className="border-t border-slate-800 pt-2 mt-2">
                  <button
                    id="logout-btn"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-rose-400 hover:bg-rose-500/10 transition font-semibold"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Güvenli Çıkış Yap</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
